import React, { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Send, User, Globe, Search, Bot, CheckCircle, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import chatAgent from "@/assets/chat-agent.jpg";
import { useToast } from "@/hooks/use-toast";

type AnalysisStep = 'idle' | 'connecting' | 'fetching' | 'reading' | 'extracting' | 'analyzing' | 'building' | 'finalizing' | 'complete';

interface AnalysisStepConfig {
  id: string;
  icon: typeof Globe;
  labelKey: string;
}
interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface ChatInterfaceProps {
  initialConfig?: any;
  initialMessages?: Message[];
  onComplete: (config: any, messages: Message[], sessionId: string, agentId: string) => void;
  onBack: () => void;
  externalMessage?: string;
  onExternalMessageSent?: () => void;
}

const ChatInterface = ({ initialConfig, initialMessages, onComplete, onBack, externalMessage, onExternalMessageSent }: ChatInterfaceProps) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'he';
  
  const ANALYSIS_STEPS: AnalysisStepConfig[] = [
    { id: 'connecting', icon: Globe, labelKey: 'onboarding.chatInterface.analysis.connecting' },
    { id: 'fetching', icon: Globe, labelKey: 'onboarding.chatInterface.analysis.fetching' },
    { id: 'reading', icon: Search, labelKey: 'onboarding.chatInterface.analysis.reading' },
    { id: 'extracting', icon: Search, labelKey: 'onboarding.chatInterface.analysis.extracting' },
    { id: 'analyzing', icon: Bot, labelKey: 'onboarding.chatInterface.analysis.analyzing' },
    { id: 'building', icon: Bot, labelKey: 'onboarding.chatInterface.analysis.building' },
    { id: 'finalizing', icon: Bot, labelKey: 'onboarding.chatInterface.analysis.finalizing' },
    { id: 'complete', icon: CheckCircle, labelKey: 'onboarding.chatInterface.analysis.complete' },
  ];
  
  const getInitialMessages = (): Message[] => {
    if (initialMessages && initialMessages.length > 0) {
      return initialMessages;
    }
    return [{
      role: 'assistant',
      content: initialConfig 
        ? t('onboarding.chatInterface.initialWithConfig', { 
            businessName: initialConfig.businessInfo?.name || t('onboarding.chatInterface.yourWebsite'),
            agentName: initialConfig.name,
            description: initialConfig.description
          })
        : t('onboarding.chatInterface.greeting')
    }];
  };
  
  const { toast } = useToast();
  
  const [messages, setMessages] = useState<Message[]>(getInitialMessages());
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [conversationStep, setConversationStep] = useState(0);
  const [isHighlighted, setIsHighlighted] = useState(false);
  const [isAnalyzingUrl, setIsAnalyzingUrl] = useState(false);
  const [analysisStep, setAnalysisStep] = useState<AnalysisStep>('idle');
  const [userHasTyped, setUserHasTyped] = useState(false);
  const [previousLanguage, setPreviousLanguage] = useState(i18n.language);
  const [urlAnalyzedConfig, setUrlAnalyzedConfig] = useState<any>(null);
  const [isBuildingAgent, setIsBuildingAgent] = useState(false);
  const [showReviewPanel, setShowReviewPanel] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset messages when language changes (only if user hasn't typed yet)
  useEffect(() => {
    if (i18n.language !== previousLanguage) {
      setPreviousLanguage(i18n.language);
      
      if (!userHasTyped) {
        // Reset messages to initial greeting in new language
        setMessages([{
          role: 'assistant',
          content: initialConfig 
            ? t('onboarding.chatInterface.initialWithConfig', { 
                businessName: initialConfig.businessInfo?.name || t('onboarding.chatInterface.yourWebsite'),
                agentName: initialConfig.name,
                description: initialConfig.description
              })
            : t('onboarding.chatInterface.greeting')
        }]);
        setConversationStep(0);
      }
    }
  }, [i18n.language, previousLanguage, userHasTyped, initialConfig, t]);

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Highlight input when component mounts
    const timer = setTimeout(() => {
      inputRef.current?.focus();
      setIsHighlighted(true);
      setTimeout(() => setIsHighlighted(false), 2000);
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);

  // Handle external message from quick prompts
  useEffect(() => {
    if (externalMessage && !isTyping && !isAnalyzingUrl) {
      setInput(externalMessage);
      onExternalMessageSent?.();
      // Trigger send after a brief delay to show the input
      setTimeout(() => {
        const sendButton = document.getElementById('chat-send-button');
        if (sendButton) sendButton.click();
      }, 100);
    }
  }, [externalMessage]);

  // Check if input contains a URL
  const extractUrl = (text: string): string | null => {
    const urlRegex = /(https?:\/\/[^\s]+)|(www\.[^\s]+)|([a-zA-Z0-9][-a-zA-Z0-9]*\.[a-zA-Z]{2,}(?:\/[^\s]*)?)/gi;
    const match = text.match(urlRegex);
    if (match) {
      let url = match[0];
      if (!url.startsWith('http')) {
        url = 'https://' + url;
      }
      return url;
    }
    return null;
  };

  // Analyze URL and build agent config with detailed progress
  const analyzeUrlAndBuildAgent = async (url: string): Promise<any> => {
    setIsAnalyzingUrl(true);
    setAnalysisStep('connecting');
    
    try {
      // Step 1: Connecting to website
      await new Promise(resolve => setTimeout(resolve, 3000));
      setAnalysisStep('fetching');
      
      // Step 2: Fetching content
      await new Promise(resolve => setTimeout(resolve, 5000));
      setAnalysisStep('reading');
      
      // Step 3: Reading website content
      await new Promise(resolve => setTimeout(resolve, 5000));
      setAnalysisStep('extracting');
      
      // Step 4: Extracting business info - Start the actual API call here
      const { data, error } = await supabase.functions.invoke('analyze-url', {
        body: { url }
      });

      if (error) throw error;
      
      // Step 5: Analyzing with AI
      setAnalysisStep('analyzing');
      await new Promise(resolve => setTimeout(resolve, 5000));
      
      // Step 6: Building agent configuration
      setAnalysisStep('building');
      await new Promise(resolve => setTimeout(resolve, 5000));
      
      // Step 7: Finalizing
      setAnalysisStep('finalizing');
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      if (data.success && data.config) {
        setAnalysisStep('complete');
        await new Promise(resolve => setTimeout(resolve, 2000));
        return data.config;
      } else {
        throw new Error(data.error || 'Failed to analyze website');
      }
    } catch (error) {
      console.error('Error analyzing URL:', error);
      throw error;
    } finally {
      setIsAnalyzingUrl(false);
      setAnalysisStep('idle');
    }
  };

  const getAIResponse = async (userMessage: string, allMessages: Message[]): Promise<string> => {
    try {
      // Get language name for the AI
      const languageNames: Record<string, string> = {
        en: 'English', es: 'Spanish', fr: 'French', de: 'German',
        pt: 'Portuguese', ar: 'Arabic', ja: 'Japanese', hi: 'Hindi',
        zh: 'Chinese', he: 'Hebrew'
      };
      const currentLanguage = languageNames[i18n.language] || 'English';

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chat`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({
            messages: allMessages.map(msg => ({
              role: msg.role,
              content: msg.content
            })),
            language: currentLanguage
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to get AI response');
      }

      const data = await response.json();
      
      return data.reply;
    } catch (error) {
      console.error('Error getting AI response:', error);
      throw error;
    }
  };

  const handleSend = async () => {
    if (!input.trim() || isTyping || isAnalyzingUrl) return;

    // Mark that user has typed
    setUserHasTyped(true);

    const userMessage: Message = {
      role: 'user',
      content: input
    };

    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    const currentInput = input;
    setInput('');

    // Check if user sent a URL
    const detectedUrl = extractUrl(currentInput);
    
    if (detectedUrl) {
      // URL detected - analyze it
      const analyzingMessage: Message = {
        role: 'assistant',
        content: t('onboarding.chatInterface.urlDetected', { url: detectedUrl })
      };
      setMessages(prev => [...prev, analyzingMessage]);
      
      try {
        const config = await analyzeUrlAndBuildAgent(detectedUrl);
        
        // Store the config in state and show review panel
        setUrlAnalyzedConfig(config);
        setShowReviewPanel(true);
        
        // Small delay to let user see the review panel first
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Then add the follow-up question about language
        const continueMessage: Message = {
          role: 'assistant',
          content: t('onboarding.chatInterface.urlSuccessContinue')
        };
        setMessages(prev => [...prev, continueMessage]);
        setConversationStep(prev => prev + 1);
      } catch (error) {
        const errorMessage: Message = {
          role: 'assistant',
          content: t('onboarding.chatInterface.urlError')
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    } else {
      // Regular chat flow
      setIsTyping(true);
      
      try {
        const aiReply = await getAIResponse(currentInput, updatedMessages);
        
        const aiResponse: Message = {
          role: 'assistant',
          content: aiReply
        };

        setMessages(prev => [...prev, aiResponse]);
        setConversationStep(prev => prev + 1);
      } catch (error) {
        console.error('Error sending message:', error);
        const errorMessage: Message = {
          role: 'assistant',
          content: t('onboarding.chatInterface.error')
        };
        setMessages(prev => [...prev, errorMessage]);
      } finally {
        setIsTyping(false);
        // Re-focus input after AI responds
        setTimeout(() => inputRef.current?.focus(), 100);
      }
    }
  };

  // Build agent directly via the intro-agent endpoint
  const buildAgentFromConversation = async (): Promise<{ config: any; sessionId: string; agentId: string }> => {
    try {
      setIsBuildingAgent(true);
      
      // Get language name for the AI
      const languageNames: Record<string, string> = {
        en: 'English', es: 'Spanish', fr: 'French', de: 'German',
        pt: 'Portuguese', ar: 'Arabic', ja: 'Japanese', hi: 'Hindi',
        zh: 'Chinese', he: 'Hebrew'
      };
      const currentLanguage = languageNames[i18n.language] || 'English';

      // First extract config from conversation
      const { data: extractData, error: extractError } = await supabase.functions.invoke('extract-agent-config', {
        body: { messages, language: currentLanguage }
      });

      let extractedConfig = extractData?.config;
      
      if (extractError || !extractedConfig) {
        // Fallback to basic extraction
        const userMessages = messages.filter(m => m.role === 'user');
        const businessName = userMessages.length > 0 ? userMessages[0].content.trim() : 'My Business';
        
        extractedConfig = {
          name: 'Alex',
          businessName,
          businessInfo: { name: businessName, type: 'general' },
          purpose: 'support',
          callType: 'both',
          language: i18n.language || 'en',
          voiceGender: 'female',
          tone: 'professional',
          features: ['transfer-to-human', 'faq'],
          capabilities: ['Answer customer questions', 'Transfer to human when needed'],
          description: `AI phone agent for ${businessName}`,
          greeting: `Hello! Thank you for calling ${businessName}. How can I help you today?`,
        };
      }

      // Merge with URL analyzed config if available
      let finalConfig = extractedConfig;
      if (urlAnalyzedConfig) {
        finalConfig = {
          ...urlAnalyzedConfig,
          name: extractedConfig.name || urlAnalyzedConfig.name,
          language: extractedConfig.language || urlAnalyzedConfig.language || 'en',
          voiceGender: extractedConfig.voiceGender || urlAnalyzedConfig.voiceGender || 'female',
          tone: extractedConfig.tone || urlAnalyzedConfig.tone || 'professional',
          greeting: extractedConfig.greeting || urlAnalyzedConfig.greeting,
          description: extractedConfig.description || urlAnalyzedConfig.description,
        };
      } else if (initialConfig) {
        finalConfig = {
          ...initialConfig,
          name: extractedConfig.name || initialConfig.name,
          language: extractedConfig.language || initialConfig.language || 'en',
          voiceGender: extractedConfig.voiceGender || initialConfig.voiceGender || 'female',
          tone: extractedConfig.tone || initialConfig.tone || 'professional',
          greeting: extractedConfig.greeting || initialConfig.greeting,
          description: extractedConfig.description || initialConfig.description,
        };
      }

      // Add conversation history with context
      finalConfig.conversationHistory = messages;
      finalConfig.knowledgeBase = `
CONVERSATION CONTEXT:
This is a conversation between a potential customer who wants to create an AI phone agent and our agent builder assistant.
The assistant asked questions to understand the customer's business needs, and the customer provided information about their business.
Use this conversation to understand the agent requirements and configure the agent accordingly.

CONVERSATION:
${messages.map(m => `${m.role === 'user' ? 'Customer' : 'Agent Builder'}: ${m.content}`).join('\n')}
`;

      // Generate a unique session ID
      const newSessionId = crypto.randomUUID();

      // Call the intro-agent endpoint to build the agent
      const { data, error } = await supabase.functions.invoke('intro-agent', {
        body: { 
          sessionId: newSessionId,
          agentConfig: finalConfig
        }
      });

      if (error) throw error;
      
      if (!data?.success) {
        throw new Error(data?.error || 'Failed to create agent');
      }

      return {
        config: finalConfig,
        sessionId: data.sessionId || newSessionId,
        agentId: data.agentId || ''
      };
    } catch (error) {
      console.error('Error building agent:', error);
      toast({
        title: "Error",
        description: "Failed to build your agent. Please try again.",
        variant: "destructive"
      });
      throw error;
    } finally {
      setIsBuildingAgent(false);
    }
  };

  const handleComplete = async () => {
    try {
      const result = await buildAgentFromConversation();
      onComplete(result.config, messages, result.sessionId, result.agentId);
    } catch (error) {
      // Error already handled in buildAgentFromConversation
      console.error('Failed to complete agent creation:', error);
    }
  };
  
  // Show test button based on conversation content - check for completion phrase
  const lastAssistantMessage = messages.filter(m => m.role === 'assistant').slice(-1)[0]?.content || '';
  const hasCompletionPhrase = lastAssistantMessage.toLowerCase().includes('test agent') || 
                              lastAssistantMessage.toLowerCase().includes('test your agent') ||
                              lastAssistantMessage.toLowerCase().includes('test my agent') ||
                              lastAssistantMessage.toLowerCase().includes('try it out') ||
                              lastAssistantMessage.includes('בדוק את הסוכן') ||
                              lastAssistantMessage.includes('לנסות אותו');
  
  // Show test button only when AI says agent is ready, or after enough conversation steps
  const hasEnoughInfo = initialConfig || hasCompletionPhrase || conversationStep >= 6;

  return (
    <div className="max-w-4xl mx-auto animate-fade-in px-4">
      <Card className="bg-card/80 backdrop-blur-sm border-border shadow-2xl">
        <div className="flex flex-col" style={{ height: 'calc(100vh - 650px)', minHeight: '300px', maxHeight: '400px' }}>
          {/* Messages Area */}
          <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-3 md:p-6 space-y-3 md:space-y-4">
            {messages.map((message, idx) => {
              // Check if this message is the "urlDetected" message (to show review panel after it)
              const isUrlDetectedMessage = message.role === 'assistant' && 
                (message.content.includes('detected a website URL') || message.content.includes('זיהיתי כתובת אתר'));
              
              // Check if the NEXT message is the urlSuccessContinue (meaning review should show after this message)
              const nextMessage = messages[idx + 1];
              const shouldShowReviewAfterThis = isUrlDetectedMessage && showReviewPanel && urlAnalyzedConfig && !isAnalyzingUrl;
              
              return (
                <React.Fragment key={idx}>
                  <div
                    className={`flex gap-2 md:gap-3 ${
                      message.role === 'user' ? 'justify-end' : 'justify-start'
                    } ${isRTL ? 'flex-row-reverse' : ''}`}
                  >
                    {message.role === 'assistant' && (
                      <div className="w-7 h-7 md:w-8 md:h-8 rounded-full overflow-hidden flex-shrink-0">
                        <img src={chatAgent} alt="AI" className="w-full h-full object-cover" />
                      </div>
                    )}
                    
                    <div
                      className={`p-3 md:p-4 rounded-2xl text-sm md:text-base ${
                        message.role === 'user'
                          ? 'bg-primary text-primary-foreground max-w-[85%] md:max-w-[80%]'
                          : 'bg-card border border-border w-full'
                      } ${isRTL ? 'text-right' : ''}`}
                    >
                      <p className="whitespace-pre-wrap">{message.content}</p>
                    </div>

                    {message.role === 'user' && (
                      <div className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                        <User className="h-3.5 w-3.5 md:h-4 md:w-4 text-foreground" />
                      </div>
                    )}
                  </div>
                  
                  {/* Show review panel inline after the URL detected message */}
                  {shouldShowReviewAfterThis && (
                    <div className="flex gap-2 md:gap-3">
                      <div className="w-7 h-7 md:w-8 md:h-8 flex-shrink-0" /> {/* Spacer for alignment */}
                      <div className="bg-gradient-to-r from-primary/10 to-secondary/10 p-4 md:p-5 rounded-2xl border border-primary/30 w-full">
                        <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
                          <CheckCircle className="h-5 w-5 text-primary" />
                          {t('onboarding.chatInterface.reviewTitle')}
                        </h3>
                        
                        <div className="space-y-3 text-sm">
                          <div className="grid grid-cols-2 gap-2">
                            <div className="bg-card/50 p-3 rounded-lg">
                              <span className="text-muted-foreground">{t('onboarding.chatInterface.reviewAgentName')}</span>
                              <p className="font-medium text-foreground">{urlAnalyzedConfig.name}</p>
                            </div>
                            <div className="bg-card/50 p-3 rounded-lg">
                              <span className="text-muted-foreground">{t('onboarding.chatInterface.reviewBusiness')}</span>
                              <p className="font-medium text-foreground">{urlAnalyzedConfig.businessInfo?.name || urlAnalyzedConfig.businessName}</p>
                            </div>
                          </div>
                          
                          <div className="bg-card/50 p-3 rounded-lg">
                            <span className="text-muted-foreground">{t('onboarding.chatInterface.reviewDescription')}</span>
                            <p className="font-medium text-foreground mt-1">{urlAnalyzedConfig.description}</p>
                          </div>
                          
                          <div className="bg-card/50 p-3 rounded-lg">
                            <span className="text-muted-foreground">{t('onboarding.chatInterface.reviewGreeting')}</span>
                            <p className="font-medium text-foreground mt-1 italic">"{urlAnalyzedConfig.greeting}"</p>
                          </div>
                          
                          {urlAnalyzedConfig.capabilities && urlAnalyzedConfig.capabilities.length > 0 && (
                            <div className="bg-card/50 p-3 rounded-lg">
                              <span className="text-muted-foreground">{t('onboarding.chatInterface.reviewCapabilities')}</span>
                              <ul className="mt-1 space-y-1">
                                {urlAnalyzedConfig.capabilities.slice(0, 4).map((cap: string, capIdx: number) => (
                                  <li key={capIdx} className="font-medium text-foreground flex items-center gap-2">
                                    <span className="w-1.5 h-1.5 rounded-full bg-primary"></span>
                                    {cap}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}

            {isTyping && (
              <div className="flex gap-2 md:gap-3">
                <div className="w-7 h-7 md:w-8 md:h-8 rounded-full overflow-hidden flex-shrink-0">
                  <img src={chatAgent} alt="AI" className="w-full h-full object-cover" />
                </div>
                <div className="bg-card p-3 md:p-4 rounded-2xl border border-border">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                </div>
              </div>
            )}

            {/* URL Analysis Progress */}
            {isAnalyzingUrl && (
              <div className="flex gap-2 md:gap-3">
                <div className="w-7 h-7 md:w-8 md:h-8 rounded-full overflow-hidden flex-shrink-0">
                  <img src={chatAgent} alt="AI" className="w-full h-full object-cover" />
                </div>
                <div className="bg-card p-4 md:p-5 rounded-2xl border border-border w-full max-w-sm">
                  <div className="space-y-3">
                    {ANALYSIS_STEPS.map((step, index) => {
                      const StepIcon = step.icon;
                      const stepIndex = ANALYSIS_STEPS.findIndex(s => s.id === analysisStep);
                      const currentStepIndex = ANALYSIS_STEPS.findIndex(s => s.id === step.id);
                      const isActive = step.id === analysisStep;
                      const isComplete = currentStepIndex < stepIndex;
                      const isPending = currentStepIndex > stepIndex;
                      
                      return (
                        <div 
                          key={step.id}
                          className={`flex items-center gap-3 transition-all duration-300 ${
                            isActive ? 'opacity-100' : isComplete ? 'opacity-70' : 'opacity-40'
                          }`}
                        >
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                            isComplete ? 'bg-primary text-primary-foreground' : 
                            isActive ? 'bg-primary/20 text-primary' : 
                            'bg-muted text-muted-foreground'
                          }`}>
                            {isComplete ? (
                              <CheckCircle className="h-4 w-4" />
                            ) : isActive ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <StepIcon className="h-4 w-4" />
                            )}
                          </div>
                          <span className={`text-sm ${isActive ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                            {t(step.labelKey)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}


            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="border-t border-border p-3 md:p-4 bg-card/50 flex-shrink-0 space-y-3">
            {/* Always show input field */}
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !isTyping && input.trim()) {
                    handleSend();
                  }
                }}
                placeholder={t('onboarding.chatInterface.placeholder')}
                className={`flex-1 bg-card text-foreground placeholder:text-muted-foreground rounded-xl h-11 md:h-12 text-sm md:text-base border-2 transition-all duration-300 ${
                  isHighlighted 
                    ? 'border-primary ring-4 ring-primary/20 animate-pulse' 
                    : 'border-border'
                }`}
                disabled={isTyping}
              />
              <Button
                id="chat-send-button"
                onClick={handleSend}
                disabled={!input.trim() || isTyping || isAnalyzingUrl}
                className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl px-4 md:px-6 h-11 md:h-12"
              >
                <Send className="h-4 w-4 md:h-5 md:w-5" />
              </Button>
            </div>
            
            {/* Show test button when ready */}
            {hasEnoughInfo && (
              <Button
                onClick={() => {
                  // Track test call event in Google Analytics
                  if (typeof window !== 'undefined' && (window as any).gtag) {
                    (window as any).gtag('event', 'test_phone_call', {
                      event_category: 'engagement',
                      event_label: 'agent_test_call',
                    });
                  }
                  handleComplete();
                }}
                disabled={isBuildingAgent}
                className="w-full bg-gradient-to-r from-primary to-secondary hover:opacity-90 text-primary-foreground py-4 md:py-5 text-base md:text-lg rounded-xl"
              >
                {isBuildingAgent ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    {t('onboarding.chatInterface.buildingAgent')}
                  </div>
                ) : (
                  t('onboarding.chatInterface.testAgent')
                )}
              </Button>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ChatInterface;
